import {
	DndContext,
	DragEndEvent,
	DragOverlay,
	PointerSensor,
	TouchSensor,
	closestCenter,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy } from '@dnd-kit/sortable';
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	styled,
} from '@mui/material';
import SortableArticlesItem from './SortableArticlesItem';
import { useTranslation } from 'react-i18next';
import { SortInformationDTO } from '@/api/DTO/information/information.interface';

interface DndArticleListSectionProps {
	informationItems: any;
	handleRepositionItem: (activeIndex: number, overIndex: number) => void;
}

export default function DndArticleListSection({
	informationItems,
	handleRepositionItem,
}: DndArticleListSectionProps) {
	const { t } = useTranslation();

	// for input methods detection
	const sensors = useSensors(useSensor(PointerSensor), useSensor(TouchSensor));

	// triggered when dragging ends
	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;
		if (!over) return;

		const activeItem = informationItems.find(
			(item: SortInformationDTO) => item.id === active.id
		);
		const overItem = informationItems.find(
			(item: SortInformationDTO) => item.id === over.id
		);

		if (!activeItem || !overItem) {
			return;
		}

		const activeIndex = informationItems.findIndex(
			(item: SortInformationDTO) => item.id === active.id
		);
		const overIndex = informationItems.findIndex(
			(item: SortInformationDTO) => item.id === over.id
		);

		if (activeIndex !== overIndex) {
			handleRepositionItem(activeIndex, overIndex);
		}
	};

	return (
		<DndContext
			sensors={sensors}
			collisionDetection={closestCenter}
			onDragEnd={handleDragEnd}
		>
			<SortableContext
				items={informationItems.map((i: { id: SortInformationDTO }) => i.id)}
				strategy={rectSortingStrategy}
			>
				<TableContainer>
					<Table>
						<TableHead>
							<TableRow
								sx={{
									backgroundColor: '#EEF1F6',
								}}
							>
								<StyledTableCell>
									{t('information.informationTitleZh')}
								</StyledTableCell>
								<StyledTableCell>
									{t('information.informationTitleEn')}
								</StyledTableCell>
								<StyledTableCell>
									{t('information.chapterNumber')}
								</StyledTableCell>
								<StyledTableCell>
									{t('information.informationStatus')}
								</StyledTableCell>
								<StyledTableCell>
									{t('information.lastUpdateTime')}
								</StyledTableCell>
								<StyledTableCell>{t('information.createTime')}</StyledTableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{informationItems?.map((item: SortInformationDTO) => (
								<SortableArticlesItem key={item.id} item={item} />
							))}
						</TableBody>
					</Table>
				</TableContainer>
				<DragOverlay />
			</SortableContext>
		</DndContext>
	);
}

const StyledTableCell = styled(TableCell)({
	fontWeight: 'normal',
	minWidth: '190px',
});
